<template>
  <main>
    <Loader
      :loader="formLoader"
      label="Carregando..."
      loaderType="progress"
      loaderSize="h-96"
    />

    <section v-if="!formLoader" class="bg-h-primary-50 border py-2">
      <div class="container mx-auto">
        <BreadcrumbComponent :home="home" :items="items" />
      </div>
    </section>

    <section
      v-if="!formLoader"
      class="container mx-auto flex w-12/12 sm:w-10/12 lg:w-8/12 justify-center my-10"
    >
      <div class="w-full">
        <form @submit.prevent="submitQuestion" class="w-auto">
          <div class="flex justify-between items-center mb-10">
            <p class="text-xl md:text-2xl font-bold">
              {{
                $route.params.id
                  ? $t("quiz.teacher.edit")
                  : $t("quiz.teacher.add")
              }}
            </p>
            <div class="flex items-center gap-2">
              <Button
                id="btn-cancel"
                :label="$t('labels.cancel')"
                class="btn-outline p-button-sm"
                @click="confirmModal()"
              />
              <Button
                id="btn-submit-questions"
                :label="$t('labels.save')"
                class="btn-primary p-button-sm"
                type="submit"
              />
            </div>
          </div>

          <div
            id="question-type-card"
            class="shadow p-5 rounded bg-white flex flex-col gap-8 items-center justify-center mb-10"
          >
            <p class="text-lg font-bold self-start">{{ "Formato*" }}</p>

            <div
              id="type"
              class="bg-h-primary-50 border-2 border-h-primary-400 text-h-primary-400 rounded p-5 text-center text-xl flex items-center justify-center gap-2"
            >
              <v-icon :name="getQuestionType()?.icon" scale="2" />
              {{ getQuestionType()?.name }}
            </div>

            <div
              class="bg-gray-50 p-5 text-center w-full text-lg text-gray-600"
            >
              {{ getQuestionType()?.info }}
            </div>
          </div>

          <div class="flex flex-col gap-10">
            <div id="prompt" class="flex flex-col gap-3">
              <div class="p-5 bg-white rounded-md shadow-sm border">
                <div id="prompt">
                  <p class="mb-2 text-lg font-bold">
                    {{ $t("question.prompt.title") }}*
                  </p>
                  <Textarea
                    id="description-input"
                    :autoResize="true"
                    rows="5"
                    cols="30"
                    :placeholder="$t('question.prompt.empty')"
                    v-model="form.prompt"
                    :class="v$.form.prompt.$error ? 'p-invalid' : ''"
                    :maxlength="999"
                  />
                  <Errors
                    :errors="v$.form.prompt.$errors"
                    :actualLength="form?.prompt?.length"
                    :maxLength="999"
                  />
                </div>
              </div>
            </div>

            <div
              v-if="questionStore.isAdaptive"
              id="material-reference"
              class="p-5 bg-white rounded-md shadow-sm border"
            >
              <div class="my-4">
                <p class="mb-5 font-bold text-lg">
                  {{ $t("question.reference") }}*
                </p>
                <p class="mb-2">{{ $t("question.selectMaterials") }}</p>
                <AutoComplete
                  id="contents-input"
                  optionLabel="name"
                  v-model="form.contents"
                  :suggestions="autocomplete.suggestions"
                  :multiple="true"
                  :forceSelection="true"
                  :delay="500"
                  @complete="searchForContents($event)"
                  @change="createContents($event)"
                  @item-select="updateContents($event)"
                  @item-unselect="clearContents($event)"
                  :class="`${
                    v$.form.contents.$error ? 'p-invalid' : ''
                  } w-full`"
                />

                <Errors :errors="v$.form.contents.$errors" />
              </div>

              <div
                class="my-4"
                v-for="(item, i) in form.materialReferences"
                :key="i"
              >
                <div class="flex items-center gap-5">
                  <span class="w-fit line-clamp-1">
                    {{ item.name ?? form.contents?.filter((e) => e)[i]?.name }}
                  </span>

                  <div class="w-12/12 md:w-2/12">
                    <InputMask
                      id="duration-input"
                      mask="99:99:99"
                      v-model="item.referenceTime"
                      placeholder="00:00:00"
                      :class="
                        v$.form.materialReferences?.$each.$response.$errors &&
                        v$.form.materialReferences?.$each.$response.$errors[i]
                          .referenceTime?.length !== 0 &&
                        v$.form.$error
                          ? 'p-invalid'
                          : ''
                      "
                    />
                    <Errors
                      v-if="v$.form.materialReferences.$each.$response.$errors"
                      :errors="
                        v$.form.materialReferences.$each.$response.$errors[i]
                          .referenceTime
                      "
                    >
                      <template #msg>
                        {{ $t("validations.required") }}
                      </template>
                    </Errors>
                  </div>
                </div>
              </div>
            </div>

            <QuestionComponent />
          </div>
        </form>
      </div>
    </section>
    <ConfirmDialog />
  </main>
</template>

<script lang="ts">
import QuestionComponent from "@/components/Question/index.vue";
import { Chapter } from "@/models/chapter.model";
import { QueryParams } from "@/models/query-params.model";
import {
  ItemQuestion,
  ItemQuestionTrueFalse,
  MaterialReferences,
  Question,
  QuestionTrueFalse,
  QuestionDragDrop,
  ItemQuestionDragNDrop,
} from "@/models/quiz.model";
import { QuizRest } from "@/services/rest/quiz.rest";
import { QuestionType, useQuizQuestionsStore } from "@/store/quiz-questions";

import { GenericsUtils } from "@/utils/generics.util";
import {
  getItemLocalStorage,
  removeItemLocalStorage,
} from "@/utils/save-storage.util";
import useVuelidate from "@vuelidate/core";
import { defineComponent } from "vue";
import { ObjectiveService } from "../../objective.service";
import { QuizServiceTeacher } from "../quiz.service";
import { QuestionFactory } from "./question.factory";
import { ToastFactory } from "@/utils/toast-factory.util";
import { replaceAnswerTextItem } from "./question.utils";
import { useQuizQuestionsDragNDropStore } from "@/store/quiz-question-drag";
import { Messages } from "@/utils/messages.util";
import { useQuizQuestionsGapStore } from "@/store/quiz-question-gap";
import { CourseType } from "@/models/courses.model";

export default defineComponent({
  name: "formQuestion",
  components: { QuestionComponent },
  computed: {
    rest(): QuizRest {
      return new QuizRest();
    },
    service(): QuizServiceTeacher {
      return new QuizServiceTeacher();
    },
    serviceObjective(): ObjectiveService {
      return new ObjectiveService();
    },
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
    course(): any {
      return JSON.parse(getItemLocalStorage("objective"));
    },
    questionType(): Number {
      return JSON.parse(localStorage.getItem("question-type") as string);
    },
  },
  data() {
    return {
      quizId: this.$route.params.quizId as string,
      objectiveId: this.$route.params.objectiveId as string,
      questionId: this.$route.params.id as string,
      form: QuestionFactory.formQuestion() as
        | Question
        | QuestionTrueFalse
        | QuestionDragDrop,
      formLoader: true as boolean,
      questionStore: QuestionFactory.selectStore() as any,

      autocomplete: {
        suggestions: [],
        selected: null,
      } as any,
      home: {
        label: "adaptativo",
        command: () => this.$router.go(-1),
      },
      items: [
        {
          label: this.$route.params?.id
            ? this.$t("labels.generic", {
                pt: "Editar questão",
                en: "Edit question",
              })
            : this.$t("labels.generic", {
                pt: "Adicionar questão",
                en: "Add question",
              }),
        },
      ],
      selectCorrect: false as boolean,
      questionTypes: [
        {
          name: this.$t("quiz.teacher.types.multiple"),
          icon: "md-checklistrtl",
          info: this.$t("quiz.teacher.typesInfo.multiple"),
        },
        {
          name: this.$t("quiz.teacher.types.trueFalse"),
          icon: "md-libraryaddcheck-outlined",
          info: this.$t("quiz.teacher.typesInfo.trueFalse"),
        },
        {
          name: this.$t("quiz.teacher.types.dragAndDrop"),
          icon: "md-copyall",
          info: this.$t("quiz.teacher.typesInfo.dragAndDrop"),
        },
        {
          name: this.$t("quiz.teacher.types.gap"),
          icon: "co-text-shapes",
          info: this.$t("quiz.teacher.typesInfo.gap"),
        },
      ],
    };
  },
  setup() {
    const v$ = useVuelidate();
    return { v$ };
  },
  validations() {
    return QuestionFactory.rules(this.course);
  },
  mounted() {
    this.setInitialData();
    if (this.questionId) {
      this.getQuestionById();
    } else {
      Object.assign(this.questionStore.form, this.form);
    }
    this.questionStore.setV(this.v$!);
    setTimeout(() => (this.formLoader = false), 500);
  },

  unmounted() {
    useQuizQuestionsDragNDropStore.$reset();
    useQuizQuestionsGapStore.$reset();
    useQuizQuestionsStore.$reset();
  },
  watch: {
    $route() {
      removeItemLocalStorage("objective");
    },
  },
  methods: {
    confirmModal(): void {
      this.$confirm.require({
        message: this.$t("labels.generic", {
          pt: `Os dados não salvos serão perdidos! Tem certeza que desejar sair?`,
          en: `Unsaved data will be lost! Are you sure you want to quit?`,
        }),
        header: this.$t("labels.generic", {
          pt: "Sair da tela",
          en: "Exit Screen",
        }),
        acceptLabel: this.$t("labels.generic", {
          pt: "Sair",
          en: "Exit",
        }),
        rejectLabel: this.$t("labels.generic", {
          pt: "Não",
          en: "No",
        }),
        rejectClass: "btn-simple",
        acceptClass: "btn-delete-block",
        accept: () => {
          this.$router.go(-1);
        },
      });
    },
    setInitialData() {
      switch (this.questionType) {
        case QuestionType.TRUE_OR_FALSE:
          Object.assign(
            this.form.items,
            new Array(1).fill(null).map(() => new ItemQuestionTrueFalse())
          );
          break;
        case QuestionType.DRAG_AND_DROP:
          Object.assign(this.questionStore.items, []);
          break;
        case QuestionType.FILL_IN_THE_BLANKS:
          Object.assign(this.questionStore.items, []);
          break;
        default:
          this.form.items = this.form.items = new Array(4)
            .fill(null)
            .map(() => new ItemQuestion());
      }
    },

    mountItemTOS(data: Question | QuestionTrueFalse | QuestionDragDrop): void {
      Object.assign(this.form.items, this.questionStore.form.items);
      data.itemTOS = this.form.items?.map((e) => {
        e.id = e.id ?? undefined;
        e.answerType = "TEXT";
        return e;
      });
    },
    mountMaterialsReference(
      data: Question | QuestionTrueFalse | QuestionDragDrop
    ): void {
      data.materialReferences = this.form.materialReferences?.map((e) => {
        e.referenceTime = e.referenceTime ? e.referenceTime : "00:00:00";
        var objMaterial = {
          materialId: e.materialId ?? e.id,
          referenceTime: this.generics.stringToSeconds(String(e.referenceTime)),
        };
        return objMaterial;
      }) as any;
    },
    isOnItemsLengthRange(): boolean {
      const showError = (message: string) => {
        this.$toast.add(ToastFactory.error(message));
        return false;
      };
      const validationRulesLength = {
        DRAG_AND_DROP: {
          condition:
            this.questionStore.getItemsFormLength >= 3 &&
            this.questionStore.getItemsFormLength <= 5,
          message: Messages.LIMIT_ITEMS_DRAG_AND_DROP,
        },
        TRUE_OR_FALSE: {
          condition: this.form.items.length <= 5,
          message: Messages.LIMIT_ITEMS_TRUE_OR_FALSE,
        },
        FILL_IN_THE_BLANKS: {
          condition:
            this.questionStore.getItemsFormLength >= 3 &&
            this.questionStore.getItemsFormLength <= 5,
          message: Messages.LIMIT_ITEMS_GAP,
        },
      };
      const type = [
        "DRAG_AND_DROP",
        "TRUE_OR_FALSE",
        "FILL_IN_THE_BLANKS",
        "DEFAULT",
      ][`${Number(this.questionType) - 1}`];
      if (validationRulesLength[type]) {
        return (
          validationRulesLength[type].condition ||
          showError(validationRulesLength[type].message)
        );
      } else {
        return this.form.items.length === 4;
      }
    },
    submitQuestion(): void {
      this.v$.form.$touch();
      this.setQuestionItems();
      if (!this.isOnItemsLengthRange()) return;
      if (!this.v$.form.$error) {
        this.formLoader = true;
        const formData = new FormData();
        const data = QuestionFactory.formQuestion();
        data.id = this.form.id ?? undefined;
        data.promptType = "TEXT";
        data.prompt = this.form.prompt;
        data.contents = this.form.contents?.map((e) => e);

        this.mountItemTOS(data);
        this.mountMaterialsReference(data);
        this.mountQuestionOptions(data);
        this.modifyAnswerText(data);
        const strData = JSON.stringify(data);
        const jsonApp = new Blob([strData], { type: "application/json" });
        formData.append("question", jsonApp);
        if (this.questionId) {
          this.update(formData, data.type as any);
        } else {
          this.create(formData, data.type as any);
        }
      } else {
        if (
          this.questionType === QuestionType.DEFAULT &&
          this.v$.form.items.$each.$response.$errors[0].correct
        ) {
          this.selectCorrect = true;
          useQuizQuestionsStore.setSelectCorrect(true);
        }
      }
    },
    create(data: FormData, type: string): void {
      const createObservable =
        type === "DEFAULT"
          ? this.rest.createDefaultQuestion
          : this.rest.createQuestion;

      createObservable(this.quizId, data).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.QUESTION_ADDED));
          this.goBackQuiz();
        },
        error: () => {
          this.formLoader = false;
        },
        complete: () => {
          this.formLoader = false;
        },
      });
    },
    update(data: FormData, type: string): void {
      const updateObservable =
        type === "DEFAULT"
          ? this.rest.updateDefaultQuestion
          : this.rest.updateQuestion;

      updateObservable(this.quizId, this.questionId, data).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.QUESTION_UPDATED));
          this.goBackQuiz();
        },
        error: () => (this.formLoader = false),
        complete: () => (this.formLoader = false),
      });
    },

    getQuestionById(): void {
      if (!this.questionId) return;

      const questionMap = {
        [QuestionType.TRUE_OR_FALSE]: {
          observable: this.service.questionAbstract,
          fetch: () =>
            this.service.getQuestionById(this.quizId, this.questionId),
          store: useQuizQuestionsStore.form,
        },
        [QuestionType.DRAG_AND_DROP]: {
          observable: this.service.questionAbstract,
          fetch: () =>
            this.service.getQuestionById(this.quizId, this.questionId),
          store: useQuizQuestionsDragNDropStore.responseContent,
        },
        [QuestionType.FILL_IN_THE_BLANKS]: {
          observable: this.service.questionAbstract,
          fetch: () =>
            this.service.getQuestionById(this.quizId, this.questionId),
          store: useQuizQuestionsGapStore.responseContent,
        },
        default: {
          observable: this.service.question,
          fetch: () =>
            this.service.getQuestionDefaultById(this.quizId, this.questionId),
          store: useQuizQuestionsStore.form,
        },
      };

      const { observable, fetch, store } =
        questionMap[`${this.questionType}`] || questionMap.default;

      observable.subscribe({
        next: (response: any) => {
          Object.assign(this.form, { ...response });
          Object.assign(store, { ...response });
        },
        error: () => {
          this.$router.go(-1);
        },
      });

      fetch();
    },

    getChapter(): Chapter {
      return JSON.parse(getItemLocalStorage("objective") as string);
    },
    searchForContents(event: any): void {
      const { query } = event;
      const params = new QueryParams();
      params.name = query;
      this.serviceObjective.materials.subscribe({
        next: (response) => {
          this.autocomplete.suggestions = response.content?.filter((e: any) => {
            const isAlready = this.generics.isAlreadyExists(
              e,
              this.form.contents
            );
            return !isAlready ? e?.name.toLowerCase() : null;
          });
        },
      });
      this.serviceObjective.getMaterialsFromObjective(this.objectiveId, params);
    },
    setQuestionItems() {
      switch (this.questionType) {
        case QuestionType.DRAG_AND_DROP:
          this.setDragNDropItems();
          break;
        case QuestionType.FILL_IN_THE_BLANKS:
          this.setGapItems();
          break;
      }
    },
    setDragNDropItems() {
      const newItems = this.questionStore.items.map((item, index) => ({
        answerText: item.getHTML(),
        correctOptions: this.questionStore.responses
          .filter((response) => response.itemGroup === index)
          .map((r) => JSON.stringify({ name: r.name, id: r.id })),
      }));

      Object.assign(this.questionStore.form.items, newItems);
    },
    setGapItems() {
      const newItems = this.questionStore.items.map((item, index) => ({
        answerText: item.getHTML(),
        correctOptions: this.questionStore.responses
          .filter((response) => response.itemGroup === index)
          .map((r) => JSON.stringify({ name: r.name, id: r.id })),
      }));

      Object.assign(this.questionStore.form.items, newItems);
    },
    modifyAnswerText(data: any) {
      switch (this.questionType) {
        case QuestionType.DRAG_AND_DROP:
        case QuestionType.FILL_IN_THE_BLANKS:
          replaceAnswerTextItem(data);
          break;
      }
    },
    mountQuestionOptions(data: any) {
      switch (this.questionType) {
        case QuestionType.DRAG_AND_DROP:
          data.extraOptions = [
            ...this.questionStore.options.map((option) =>
              JSON.stringify({ name: option.name, id: option.id })
            ),
          ];
          break;
      }
    },
    updateContents(event: any): void {
      if (this.questionId) {
        const data = new MaterialReferences();
        data.materialId = event.value.id;
        data.referenceTime = event.value.referenceTime;
        this.form.materialReferences?.push({ ...data });
      }
    },
    createContents(event: any): void {
      if (!this.questionId) this.form.materialReferences = event.value;
    },
    clearContents(event: any): void {
      if (this.questionId) {
        var index;
        this.form.materialReferences?.forEach((e, i) => {
          index = i;
        });
        this.form.materialReferences?.splice(index, 1);
      } else {
        const refs = this.form.materialReferences;
        const filtered = refs?.filter((e) => e.materialId !== event.value.id);
        this.form.materialReferences = filtered;
      }
    },
    chooseCorrectItem(item: ItemQuestion, items: any): void {
      if (this.questionType !== QuestionType.DEFAULT) return;
      items = items.map((e) => {
        this.form.correctItem = null;
        e.correct = false;
        return e;
      });
      item.correct = true;
      this.selectCorrect = false;
      useQuizQuestionsStore.setSelectCorrect(false);
    },
    goBackQuiz() {
      const componentName =
        this.course.type === CourseType.ADAPTATIVE
          ? "formObjective"
          : "formChapterTraditional";

      const paramsQuiz = history.state?.paramsQuiz || {};
      this.$router.push({
        name: componentName,
        params: paramsQuiz,
      });
    },
    getQuestionType(): any {
      switch (this.questionType) {
        case QuestionType.TRUE_OR_FALSE:
          return this.questionTypes[1];
        case QuestionType.DRAG_AND_DROP:
          return this.questionTypes[2];
        case QuestionType.FILL_IN_THE_BLANKS:
          return this.questionTypes[3];
        default:
          return this.questionTypes[0];
      }
    },
  },
});
</script>
